/**
 * Sim Mode — read-only state-simulation harness for QA'ing booth pages on prod.
 *
 * Entered by visiting `/:projectId/sim` (see pages/sim/SimHost.tsx). Once enabled,
 * the flag is STICKY for the lifetime of the tab: navigating out of `/sim` into the
 * real flow keeps every write guard active, so a seeded fake session can never leak
 * into prod data. The only way out is a full page reload (the SimHost "Exit" button
 * does `location.assign`, which reloads).
 *
 * Two layers of protection:
 *  1. Targeted guards — sessionState log flush, session upload, booth-event enqueue
 *     and printing early-return when `isSimulationActive()`.
 *  2. A global fetch write-blocker (installed here) that rejects every non-GET/HEAD
 *     request with a synthetic 418, catching anything the targeted guards miss
 *     (payment creation, media uploads, telemetry, ...).
 *
 * This module must stay dependency-free: it is imported by low-level stores/utils
 * and must never create an import cycle.
 */

let simulationActive = false
let fetchGuardInstalled = false

export function isSimulationActive(): boolean {
  return simulationActive
}

export function enableSimulationMode(): void {
  if (simulationActive) return
  simulationActive = true
  installFetchWriteGuard()
  console.warn(
    '[SIM] Simulation mode enabled — read-only. All non-GET API requests are blocked until this tab reloads.'
  )
}

function installFetchWriteGuard(): void {
  if (fetchGuardInstalled || typeof window === 'undefined') return
  fetchGuardInstalled = true

  const realFetch = window.fetch.bind(window)
  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> => {
    const method = (
      init?.method ||
      (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET') ||
      'GET'
    ).toUpperCase()

    if (simulationActive && method !== 'GET' && method !== 'HEAD') {
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.toString()
            : (input as Request).url
      console.warn(`[SIM] Blocked ${method} ${url} — simulation is read-only`)
      return new Response(JSON.stringify({ simulated: true, blocked: true }), {
        // Distinctive non-2xx so calling code treats it as a failed request
        // instead of parsing a fake success payload.
        status: 418,
        statusText: 'Simulation (read-only)',
        headers: { 'Content-Type': 'application/json' }
      })
    }

    return realFetch(input as RequestInfo, init)
  }
}
